import { useState, useCallback } from "react";
import { useBackOrNavigate } from "../../../../common/utils/navigateUtils";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { processError } from "../../../../common/utils/base_error";
import { useLocation } from "react-router";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { GridRowSelectionModel } from "@mui/x-data-grid-pro";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { useUserAuthStore } from "../../../../stores/global/userAuth";
import { FuncionarioInformation } from "../../../../common/domain/models/funcionario_information";
import { useReportViewer } from "../../../../config/hooks/utils/report_viewer";
import { InspecaoPatrimonioModel } from "../../domain/models/inspecao_patrimonio";
import { initialFiltersValues, useInspecaoPatrimonioListaFilter } from "../hooks/PatrimonioItensInspecao_cadastro/filters";
import { useInspecaoPatrimonioListaPagination } from "../hooks/PatrimonioItensInspecao_cadastro/pagination";
import { InspecaoPatrimonioForm } from "../schemas/InspecaoPatrimonio_cadastro/inspecao_patrimonio_cadastro";
import { PatrimonioChecklistInfo } from "../../domain/models/patrimonio_checklist_info";

/**
 * LoadInspecaoPatrimonioFuncState
 */

type LoadInspecaoPatrimonioFuncState = {
  idPatrimonioMaquinaEquip?: number;
  status?: number | null;
  dataDe?: Date | null;
  dataAte?: Date | null;

  page?: number;
  rowsPerPage?: number;
};

/**
 * useObraItensChecklistAreaCadastroViewModel
 */

export const usePatrimonioItensInspecaoCadastroViewModel = () => {
  const location = useLocation();

  const { idUsuario } = useUserAuthStore();

  const {
    commons: {
      getFuncionarioByIdUsuarioUseCase,
    },
    controlePatrimonio: {

      getPatrimonioChecklistMaquinaEquipByIdUseCase,
      getInspecaoPatrimonioByIdUseCase,
      getAllInspecaoPatrimonioUseCase,
      createInspecaoPatrimonioUseCase,
      updateInspecaoPatrimonioUseCase,
      deleteInspecaoPatrimonioUseCase,
      getPatrimonioChecklistInfoUseCase,
      // copyInspecaoPatrimonioUseCase, IMPLEMENTAR
    }
  } = useDependencyInjection();

  const {
    pushInfoNotification,
    pushWarnNotification,
    pushErrorNotification,
    setPageSaving,
  } = usePageLoader();

  const backOrNavigate = useBackOrNavigate();
  const debouncedRef = useCreateDebounceHandler(1500);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const [funcionarioInformation, setFuncionarioInformation] = useState<FuncionarioInformation>();

  const [idPatrimonioCentroCusto, setIdPatrimonioCentroCusto] = useState<number>();

  const [idPatrimonioMaquinaEquip, setIdPatrimonioMaquinaEquip] = useState<number>();
  const [idPatrimonioChecklist, setIdPatrimonioChecklist] = useState<number>();

  const [idInspecoesPatrimonioDelete, setidInspecoesPatrimonioDelete] = useState<number | undefined>();

  const [inspecoesPatrimonio, setInspecoesPatrimonio] = useState<InspecaoPatrimonioModel[]>();
  const [selectedInspecaoPatrimonio, setSelectedInspecaoPatrimonio] = useState<InspecaoPatrimonioModel>();

  const [statusChecklistServico, setStatusChecklistServico] = useState<number>(0);
  const [metaChecklistServico, setMetaChecklistServico] = useState<number>(0);
  const [patrimonioChecklistInfo, setPatrimonioChecklistInfo] = useState<PatrimonioChecklistInfo>();

  const [inspecaoPatrimonioModal, setInspecaoPatrimonioModal] = useState(false);
  const [inspecaoPatrimonioAprovacaoModal, setInspecaoPatrimonioAprovacaoModal] = useState(false);
  const [inspecaoPatrimonioDeleteModal, setInspecaoPatrimonioDeleteModal] = useState(false);


  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>();
  const filters = useInspecaoPatrimonioListaFilter();
  const pagination = useInspecaoPatrimonioListaPagination();

  const [reportViewerState, startReportViewerProcess] = useReportViewer();

  /**
   * Funcoes de carregamento
   */

  const loadPatrimonioChecklistInfo = useCallback(async (idPatrimonioCentroCusto: number, idPatrimonioMaquinaEquip?: number, idInspecaoPatrimonio?: number) => {
    try {
      const response = await getPatrimonioChecklistInfoUseCase.getPatrimonioChecklistInfo(
        idPatrimonioCentroCusto,
        idPatrimonioMaquinaEquip,
        idInspecaoPatrimonio,
      );
      setPatrimonioChecklistInfo(response);
    } catch (e: unknown) {
      throw new Error("Falha ao carregar informações do checklist do patrimônio!");
    }
  }, [
    getPatrimonioChecklistInfoUseCase,
  ]);

  const loadInspecaoPatrimonio = useCallback(async (fs?: LoadInspecaoPatrimonioFuncState) => {
    try {
      const filteredIdPatrimonioMaquinaEquip = fs?.idPatrimonioMaquinaEquip ?? idPatrimonioMaquinaEquip;
      const filteredDataDe = evaluateParamValue(fs?.dataDe, filters.dataDe);
      const filteredDataAte = evaluateParamValue(fs?.dataAte, filters.dataAte);
      const filteredStatus = evaluateParamValue(fs?.status, filters.status);

      const filteredPage = fs?.page ?? pagination.page;
      const filteredRowsPerPage = fs?.rowsPerPage ?? pagination.rowsPerPage;

      await getAllInspecaoPatrimonioUseCase.getAllInspecaoPatrimonio(
        filteredIdPatrimonioMaquinaEquip ?? 0,
        filteredStatus,
        filteredDataDe,
        filteredDataAte,
        filteredPage,
        filteredRowsPerPage,
      );

      setInspecoesPatrimonio(getAllInspecaoPatrimonioUseCase.items);
      pagination.setTotalItems(getAllInspecaoPatrimonioUseCase.totalItems);
    } catch (e: unknown) {
      setInspecoesPatrimonio(undefined);
      pagination.setTotalItems(0);
      throw new Error("Falha ao buscar dados da pagina!");
    }
  }, [
    getAllInspecaoPatrimonioUseCase,
    idPatrimonioMaquinaEquip,
    filters,
    pagination,
  ]);

  const loadFuncionarioInformation = useCallback(async () => {
    try {
      const response = await getFuncionarioByIdUsuarioUseCase.getFuncionarioByIdUsuario(idUsuario);
      setFuncionarioInformation(response ?? undefined);
    } catch (e: unknown) {
      setFuncionarioInformation(undefined);
      throw new Error('Falha ao buscar informações sobre o usuário!');
    }
  }, [
    idUsuario,
    getFuncionarioByIdUsuarioUseCase,
  ]);

  /**
   * useEffect
   */


  usePageStartBootstrap(async () => {
    try {

      const params = new URLSearchParams(location.search);
      const jobs = [];
      const locationIdPatrimonioMaquinaEquip = params.get('idPatrimonioMaquinaEquip') ?? null;
      const locationIdPatrimoniCentroCusto = params.get('id') ?? null;

      if (!locationIdPatrimoniCentroCusto || locationIdPatrimoniCentroCusto.length === 0) {
        setErrorMessage("Falha ao recuperar id do centro de custo do patrimônio!");
        return;
      }

      if (!locationIdPatrimonioMaquinaEquip || locationIdPatrimonioMaquinaEquip.length === 0) {
        setErrorMessage("Falha ao recuperar id do patrimônio máquina/equipamento!");
        return;
      }

      const idPatrimonioCentroCusto = Number(locationIdPatrimoniCentroCusto);
      const idPatrimonioMaquinaEquip = Number(locationIdPatrimonioMaquinaEquip);

      setIdPatrimonioCentroCusto(idPatrimonioCentroCusto);
      setIdPatrimonioMaquinaEquip(idPatrimonioMaquinaEquip);

      if (locationIdPatrimonioMaquinaEquip) {
        jobs.push(loadFuncionarioInformation());
        jobs.push(loadPatrimonioChecklistInfo(idPatrimonioCentroCusto, idPatrimonioMaquinaEquip));
        jobs.push(loadInspecaoPatrimonio({ idPatrimonioMaquinaEquip: idPatrimonioMaquinaEquip, page: 1 }));
      }
      await Promise.all(jobs);
    } catch (e: unknown) {
      setErrorMessage(processError(e, 'Falha ao buscar dados da pagina!')[0]);
    }
  });
  /**
   * Eventos
   */

  const handleBack = useCallback(() => {
    backOrNavigate(-1, { alternativeRoute: `/Patrimonio_cadastroInspecao?id=${idPatrimonioMaquinaEquip}` });
  }, [
    idPatrimonioMaquinaEquip,
    backOrNavigate,
  ]);

  const handleReload = useCallback(async () => {
    if (idPatrimonioMaquinaEquip) {
      try {
        setLoading(true);

        await loadFuncionarioInformation();

        await loadInspecaoPatrimonio({ idPatrimonioMaquinaEquip: idPatrimonioMaquinaEquip, page: 1 });

      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao buscar dados das paginas!'));
      } finally {
        setLoading(false);
      }
    }
  }, [
    idPatrimonioMaquinaEquip,
    loadFuncionarioInformation,
    loadInspecaoPatrimonio,
    pushErrorNotification,
  ]);

  const handleRowSelectionModelChange = useCallback((model: GridRowSelectionModel) => {
    setRowSelectionModel(model);
  }, [
  ]);

  const handleInspecaoPatrimonioAprovacaoOpenState = useCallback((openState: boolean) => {
    if (openState) {
      if (!rowSelectionModel || !rowSelectionModel.ids.size) {
        pushWarnNotification("Alerta", "Você deve selecionar pelo menos um checklist para ser aprovado!");
        return;
      }
    }

  }, [
    rowSelectionModel,
    pushWarnNotification,
  ]);

  // const handleInspecaoPatrimonioAprovacaoSubmit = useCallback(async (form: InspecaoPatrimonioAprovacaoForm) => {
  //   if (funcionarioInformation && funcionarioInformation.idFuncionario && rowSelectionModel && rowSelectionModel.ids.size) {
  //     try {
  //       setSaving(true);
  //       setPageSaving(true);

  //       await updateInspecaoPatrimonioAprovacaoLoteUseCase.updateInspecaoPatrimonioAprovacaoLote(
  //         funcionarioInformation.idFuncionario, rowSelectionModel.ids, form.data);

  //       await loadInspecaoPatrimonio();

  //       setRowSelectionModel({
  //         type: 'include',
  //         ids: new Set<GridRowId>(),
  //       });

  //       setInspecaoPatrimonioAprovacaoModal(false);

  //       pushInfoNotification('Sucesso', 'Registro salvo com sucesso!');
  //     } catch (e: unknown) {
  //       pushErrorNotification('Erro', processError(e, ''));
  //     } finally {
  //       setPageSaving(false);
  //       setSaving(false);
  //     }
  //   }
  // }, [
  //   funcionarioInformation,
  //   rowSelectionModel,
  //   updateInspecaoPatrimonioAprovacaoLoteUseCase,
  //   loadInspecaoPatrimonio,
  //   pushErrorNotification,
  //   pushInfoNotification,
  //   setPageSaving,
  // ]);

  const handleInspecaoPatrimonioOpenState = useCallback((idInspecaoPatrimonio: number | null, openState: boolean) => {
    if (openState) {
      if (idInspecaoPatrimonio && inspecoesPatrimonio) {
        setSelectedInspecaoPatrimonio(inspecoesPatrimonio.find(x => x.idInspecaoPatrimonio === idInspecaoPatrimonio));
      } else {
        setSelectedInspecaoPatrimonio(undefined);
      }
    }
    setInspecaoPatrimonioModal(openState);
  }, [
    inspecoesPatrimonio,
  ]);


  const handleInspecaoPatrimonioSubmit = useCallback(async (form: InspecaoPatrimonioForm) => {
    if (idPatrimonioMaquinaEquip) {
      try {
        setSaving(true);
        setPageSaving(true);
        if (selectedInspecaoPatrimonio) {
          await updateInspecaoPatrimonioUseCase.updateInspecaoPatrimonio(
            selectedInspecaoPatrimonio.idInspecaoPatrimonio,
            idPatrimonioMaquinaEquip ?? 0,
            null,
            null,
            form.dataInspecao ?? null,
            null,
            form.descricao ?? null,
            null,
            form.status ?? 0,
          );
        } else {
          await createInspecaoPatrimonioUseCase.createInspecaoPatrimonio(
            idPatrimonioMaquinaEquip ?? 0,
            form.dataInspecao ?? null,
            form.descricao ?? null,
          );
        }
        await loadInspecaoPatrimonio();
        setInspecaoPatrimonioModal(false);

        pushInfoNotification('Sucesso', 'Registro salvo com sucesso!');
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao salvar verificação!'));
      } finally {
        setPageSaving(false);
        setSaving(false);
      }
    }
  }, [
    idPatrimonioMaquinaEquip,
    selectedInspecaoPatrimonio,
    createInspecaoPatrimonioUseCase,
    updateInspecaoPatrimonioUseCase,
    loadInspecaoPatrimonio,
    pushErrorNotification,
    pushInfoNotification,
    setPageSaving,
  ]);

  // const handleInspecaoPatrimonioCopy = useCallback((idInspecaoPatrimonio: number | null, openState: boolean) => {
  //   if (openState) {
  //     if (idInspecaoPatrimonio && inspecoesObra) {
  //       setSelectedInspecaoPatrimonio(inspecoesObra.find(x => x.idInspecaoPatrimonio === idInspecaoPatrimonio));
  //     } else {
  //       setSelectedInspecaoPatrimonio(undefined);
  //     }
  //   }
  //   setInspecaoPatrimonioCopyModal(openState);
  // }, [
  //   inspecoesObra,
  // ]);

  // const handleInspecaoPatrimonioCopyConfirm = useCallback(async (copyNumber: number) => {
  //   if (selectedInspecaoPatrimonio) {
  //     try {
  //       setSaving(true);
  //       setPageSaving(true);

  //       await copyInspecaoPatrimonioUseCase.copyInspecaoPatrimonio(
  //         selectedInspecaoPatrimonio.idInspecaoPatrimonio,
  //         copyNumber,
  //       );

  //       await loadObraChecklistServicoEstatisticas();
  //       await loadInspecaoPatrimonio();

  //       setInspecaoPatrimonioCopyModal(false);

  //       pushInfoNotification('Sucesso', 'Registro copiado com sucesso!');
  //     } catch (e: unknown) {
  //       pushErrorNotification('Erro', processError(e, 'Falha ao copiar verificação!'));
  //     } finally {
  //       setPageSaving(false);
  //       setSaving(false);
  //     }
  //   }
  // }, [
  //   selectedInspecaoPatrimonio,
  //   copyInspecaoPatrimonioUseCase,
  //   loadObraChecklistServicoEstatisticas,
  //   loadInspecaoPatrimonio,
  //   pushErrorNotification,
  //   pushInfoNotification,
  //   setPageSaving,
  // ]);

  const handleInspecaoPatrimonioDeletar = useCallback((idInspecaoPatrimonio: number | null, openState: boolean) => {
    if (openState) {
      if (idInspecaoPatrimonio) {
        setidInspecoesPatrimonioDelete(idInspecaoPatrimonio);
      } else {
        setidInspecoesPatrimonioDelete(undefined);
      }
    }
    setInspecaoPatrimonioDeleteModal(openState);
  }, [
  ]);

  const handleInspecaoPatrimonioDeletarConfirm = useCallback(async () => {
    if (idInspecoesPatrimonioDelete) {
      try {
        setSaving(true);
        setPageSaving(true);

        await deleteInspecaoPatrimonioUseCase.deleteInspecaoPatrimonio(idInspecoesPatrimonioDelete);

        await loadInspecaoPatrimonio();
        setInspecaoPatrimonioDeleteModal(false);

        pushInfoNotification('Sucesso', 'Registro excluido com sucesso!');
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao excluir verificação!'));
      } finally {
        setPageSaving(false);
        setSaving(false);
      }
    }
  }, [
    idInspecoesPatrimonioDelete,
    deleteInspecaoPatrimonioUseCase,
    loadInspecaoPatrimonio,
    pushErrorNotification,
    pushInfoNotification,
    setPageSaving,
  ]);

  const handleEnterInspecaoPatrimonio = useCallback((idInspecaoPatrimonio: number, openNewTab: boolean) => {
    if (openNewTab) {
      window.open(`/PatrimonioItensInspecao_verificacao?id=${idPatrimonioCentroCusto}&idPatrimonioMaquinaEquip=${idPatrimonioMaquinaEquip}&idInspecaoPatrimonio=${idInspecaoPatrimonio}`, '_blank', 'noopener,noreferrer');
    } else {
      backOrNavigate(`/PatrimonioItensInspecao_verificacao?id=${idPatrimonioCentroCusto}&idPatrimonioMaquinaEquip=${idPatrimonioMaquinaEquip}&idInspecaoPatrimonio=${idInspecaoPatrimonio}`,
        { alternativeRoute: `/PatrimonioItensInspecao_cadastro?id=${idPatrimonioCentroCusto}&idPatrimonioMaquinaEquip=${idPatrimonioMaquinaEquip}` });
    }
  }, [
    idPatrimonioMaquinaEquip,
    idPatrimonioCentroCusto,
    backOrNavigate,
  ]);

  // const handleChecklistServicoStatusChange = useCallback(async (value: number) => {
  //   if (idPatrimonioChecklist) {
  //     try {
  //       setLoading(true);

  //       await updateStatusObraChecklistServicoUseCase.updateStatusObraChecklistServico(
  //         idPatrimonioChecklist, value);

  //       setStatusChecklistServico(value);

  //       pushInfoNotification('Sucesso', 'Registro salvo com sucesso!');
  //     } catch (e: unknown) {
  //       pushErrorNotification("Erro", processError(e, "Falha ao atualizar status do checklist!"));
  //     } finally {
  //       setLoading(false);
  //     }
  //   }
  // }, [
  //   idPatrimonioChecklist,
  //   updateStatusObraChecklistServicoUseCase,
  //   pushErrorNotification,
  //   pushInfoNotification,
  // ]);

  const handleChecklistServicoMetaChange = useCallback((value: number) => {
    setMetaChecklistServico(value);
  }, [
  ]);

  // const handleDataChange = useDebouncedCallback(debounceHandler, async (data: Date | null) => {
  //   useObraItensChecklistAreaCadastroFilters.setState({ data });
  //   return async () => {
  //     try {
  //       setLoading(true);
  //       useObraItensChecklistAreaCadastroPagination.setState({ page: 1 });
  //       await loadInspecaoPatrimonio({ data, page: 1 });
  //     } catch (e: unknown) {
  //       pushErrorNotification("Erro", processError(e, "Falha ao buscar dados da pagina!"));
  //     } finally {
  //       setLoading(false);
  //     }
  //   };
  // }, [
  //   loadInspecaoPatrimonio,
  //   pushErrorNotification,
  // ]);

  // const handleLocalizacaoChange = useDebouncedCallback(debounceHandler, async (localizacao: string | null) => {
  //   useObraItensChecklistAreaCadastroFilters.setState({ localizacao });
  //   return async () => {
  //     try {
  //       setLoading(true);
  //       useObraItensChecklistAreaCadastroPagination.setState({ page: 1 });
  //       await loadInspecaoPatrimonio({ localizacao, page: 1 });
  //     } catch (e: unknown) {
  //       pushErrorNotification("Erro", processError(e, "Falha ao buscar dados da pagina!"));
  //     } finally {
  //       setLoading(false);
  //     }
  //   };
  // }, [
  //   loadInspecaoPatrimonio,
  //   pushErrorNotification,
  // ]);

  // const handleStatusChange = useDebouncedCallback(debounceHandler, async (status: number) => {
  //   useObraItensChecklistAreaCadastroFilters.setState({ status });
  //   return async () => {
  //     try {
  //       setLoading(true);
  //       useObraItensChecklistAreaCadastroPagination.setState({ page: 1 });
  //       await loadInspecaoPatrimonio({ status, page: 1 });
  //     } catch (e: unknown) {
  //       pushErrorNotification("Erro", processError(e, "Falha ao buscar dados da pagina!"));
  //     } finally {
  //       setLoading(false);
  //     }
  //   };
  // }, [
  //   loadInspecaoPatrimonio,
  //   pushErrorNotification,
  // ]);

  // const handleClearFilters = useCallback(async () => {
  //   try {
  //     setLoading(true);
  //     useObraItensChecklistAreaCadastroFilters.setState({ ...initialObraItensChecklistAreaCadastroFilters });
  //     useObraItensChecklistAreaCadastroPagination.setState({ page: 1 });
  //     await loadInspecaoPatrimonio({ ...initialObraItensChecklistAreaCadastroFilters, page: 1 });
  //   } catch (e: unknown) {
  //     pushErrorNotification("Erro", processError(e, "Falha ao buscar dados da pagina!"));
  //   } finally {
  //     setLoading(false);
  //   }
  // }, [
  //   loadInspecaoPatrimonio,
  //   pushErrorNotification,
  // ]);

  const handlePageChange = useCallback(async (page: number) => {
    try {
      setLoading(true);
      pagination.setPage(page + 1);
      await loadInspecaoPatrimonio({ page: page + 1 });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao buscar dados da pagina!"));
    } finally {
      setLoading(false);
    }
  }, [
    pagination,
    loadInspecaoPatrimonio,
    pushErrorNotification,
  ]);

  const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
    try {
      setLoading(true);
      pagination.setRowsPerPage(rowsPerPage);
      await loadInspecaoPatrimonio({ page: 1, rowsPerPage });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao buscar dados da pagina!"));
    } finally {
      setLoading(false);
    }
  }, [
    pagination,
    loadInspecaoPatrimonio,
    pushErrorNotification,
  ]);


  //#region PESQUISA


  const handleStatusChange = useDebouncedCallback(debouncedRef, (status: number) => {
    filters.setFilters({ status });
    return async () => {
      try {
        setLoading(true);
        pagination.setPage(1);
        await loadInspecaoPatrimonio({ status, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao buscar itens de inspeção do patrimônio!'));
      } finally {
        setLoading(false);
      }
    };
  }, [filters, loadInspecaoPatrimonio, pushErrorNotification]);

  const handleDataRangeChange = useDebouncedCallback(
    debouncedRef,
    (dataDe: Date | null, dataAte: Date | null) => {
      filters.setFilters({ dataDe });
      filters.setFilters({ dataAte });

      return async () => {
        try {
          setLoading(true);
          pagination.setPage(1);
          await loadInspecaoPatrimonio({ dataDe, dataAte, page: 1 });
        } catch (e: unknown) {
          pushErrorNotification("Error", processError(e, "Falha ao buscar itens de inspeção do patrimônio!"));
        } finally {
          setLoading(false);
        }
      };
    },
    [loadInspecaoPatrimonio, filters, pagination]
  );

  const handleClearFilters = useCallback(async () => {
    try {
      setLoading(true);
      filters.clearFilters();
      pagination.setPage(1);
      await loadInspecaoPatrimonio({ ...initialFiltersValues, page: 1 });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, 'Falha ao buscar dados!'));
    } finally {
      setLoading(false);
    }
  }, [
    filters,
    pagination,
    loadInspecaoPatrimonio,
    pushErrorNotification,
  ]);

  //#endregion

  return {
    loading,
    saving,
    errorMessage,
    funcionarioInformation: funcionarioInformation ?? null,
    inspecoesPatrimonio: inspecoesPatrimonio ?? [],
    selectedInspecaoPatrimonio: selectedInspecaoPatrimonio ?? null,
    statusChecklistServico,
    metaChecklistServico,
    rowSelectionModel,
    filters,
    pagination,
    reportViewerState,
    inspecaoPatrimonioModal,
    inspecaoPatrimonioAprovacaoModal,
    inspecaoPatrimonioDeleteModal,
    idPatrimonioCentroCusto,
    patrimonioChecklistInfo: patrimonioChecklistInfo ?? null,

    handleBack,
    handleReload,
    handleDataRangeChange,
    handleStatusChange,
    handleClearFilters,
    handleRowSelectionModelChange,
    handleInspecaoPatrimonioAprovacaoOpenState,
    handleInspecaoPatrimonioOpenState,
    handleInspecaoPatrimonioSubmit,
    handleInspecaoPatrimonioDeletar,
    handleInspecaoPatrimonioDeletarConfirm,
    handleEnterInspecaoPatrimonio,
    handleChecklistServicoMetaChange,
    handlePageChange,
    handleRowsPerPageChange,
  };
}